import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MAINTENANCE_CREATE, MAINTENANCE_UPDATE, MAINTENANCE_GET } from '../../graphql/maintenance';
import { VEHICLES_LIST } from '../../graphql/vehicles';
import { VENDORS_LIST } from '../../graphql/vendors';
import { 
  TextField, 
  Button, 
  MenuItem, 
  Box, 
  CircularProgress, 
  Select, 
  InputLabel, 
  FormControl, 
  Paper, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip
} from '@mui/material';
import EditableFormField from '../../components/editable-form-field';
import { useNavigate } from 'react-router-dom';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { FileInput } from '@8base/file-manager';
import Iconify from '../../components/iconify';
import { PRIMARY } from '../../theme/palette';
import DatePickerAuto from '../../components/custom-datepicker/DatePickerAuto';

// Predefined maintenance categories
const maintenanceCategories = [
  { value: 'oil_change', label: 'Oil Change' },
  { value: 'tire_rotation', label: 'Tire Rotation' },
  { value: 'battery_replacement', label: 'Battery Replacement' },
  { value: 'brake_service', label: 'Brake Service' },
  { value: 'air_filter', label: 'Air Filter Replacement' },
  { value: 'transmission_service', label: 'Transmission Service' },
  { value: 'coolant_flush', label: 'Coolant Flush' },
  { value: 'spark_plugs', label: 'Spark Plugs Replacement' },
  { value: 'fuel_filter', label: 'Fuel Filter Replacement' },
  { value: 'timing_belt', label: 'Timing Belt Replacement' },
  { value: 'suspension', label: 'Suspension Service' },
  { value: 'exhaust', label: 'Exhaust System' },
  { value: 'electrical', label: 'Electrical System' },
  { value: 'body_repair', label: 'Body Repair' },
  { value: 'glass_replacement', label: 'Glass Replacement' },
  { value: 'other', label: 'Other' }
];

const MaintenanceForm = ({ recordId, vehicleId, onClose, isEditing: externalIsEditing, onToggleEdit, isModal = false }) => {
  const [internalIsEditing, setInternalIsEditing] = useState(!recordId); // Start in edit mode for new records, view mode for existing
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = onToggleEdit || setInternalIsEditing;
  
  const [form, setForm] = useState({
    description: '',
    category: '',
    cost: '',
    performedDate: '',
    scheduledDate: '',
    vendorId: '',
    vehicleId: '',
  });
  const { data: vehiclesData, loading: vehiclesLoading } = useQuery(VEHICLES_LIST);
  const { data: recordData, loading: recordLoading } = useQuery(
    MAINTENANCE_GET,
    { variables: { id: recordId }, skip: !recordId }
  );

  useEffect(() => {
    if (recordId && recordData?.maintenanceRecordsList?.items?.length) {
      const record = recordData.maintenanceRecordsList.items[0];
      setForm({
        description: record.description || '',
        category: record.category || '',
        cost: record.cost || '',
        performedDate: record.performedDate ? record.performedDate.split('T')[0] : '',
        scheduledDate: record.scheduledDate ? record.scheduledDate.split('T')[0] : '',
        vendorId: record.vendor?.id || '',
        vehicleId: record.maintenanceRecords?.id || '',
      });
    } else if (!recordId) {
      setForm({
        description: '',
        category: '',
        cost: '',
        performedDate: '',
        scheduledDate: '',
        vendorId: '',
        vehicleId: vehicleId || '', // Use passed vehicleId if available
      });
    }
  }, [recordId, recordData, vehicleId]);

  const [createMaintenance, { loading: creating }] = useMutation(MAINTENANCE_CREATE, {
    refetchQueries: ['MaintenanceRecordsList', 'VehicleGet'],
  });
  const [updateMaintenance, { loading: updating }] = useMutation(MAINTENANCE_UPDATE, {
    refetchQueries: ['MaintenanceRecordsList', 'VehicleGet'],
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling up to parent forms
    const formatDate = (dateStr) => dateStr ? new Date(dateStr).toISOString().slice(0, 10) : null;
    const data = {
      ...(recordId ? { id: recordId } : {}),
      description: form.description,
      category: form.category,
      cost: parseFloat(form.cost),
      performedDate: formatDate(form.performedDate),
      scheduledDate: formatDate(form.scheduledDate),
      vendor: form.vendorId ? { connect: { id: form.vendorId } } : undefined,
      maintenanceRecords: form.vehicleId ? { connect: { id: form.vehicleId } } : undefined,
    };
    if (recordId) {
      await updateMaintenance({ variables: { data } });
    } else {
      await createMaintenance({ variables: { data } });
    }
    if (isModal && onClose) {
      onClose(true);
    } else if (onClose) {
      onClose(true);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (recordId) {
      // Reset form to original values
      const record = recordData.maintenanceRecordsList.items[0];
      setForm({
        description: record.description || '',
        category: record.category || '',
        cost: record.cost || '',
        performedDate: record.performedDate ? record.performedDate.split('T')[0] : '',
        scheduledDate: record.scheduledDate ? record.scheduledDate.split('T')[0] : '',
        vendorId: record.vendor?.id || '',
        vehicleId: record.maintenanceRecords?.id || '',
      });
      setIsEditing(false);
    } else {
      if (isModal && onClose) {
        onClose();
      } else if (onClose) {
        onClose();
      }
    }
  };

  if (recordId && recordLoading) return <CircularProgress />;


  const vehicleOptions = vehiclesData?.vehiclesList?.items?.map(vehicle => ({
    value: vehicle.id,
    label: `${vehicle.make} ${vehicle.model} (${vehicle.licensePlate})`
  })) || [];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>

      {/* Edit Button for View Mode - Top */}
      {recordId && !isEditing && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button onClick={handleEdit} variant="contained" color="primary">
            Edit
          </Button>
        </Box>
      )}

      <EditableFormField
        type="select"
        label="Vehicle"
        name="vehicleId"
        value={form.vehicleId}
        onChange={handleChange}
        isEditing={isEditing}
        options={vehicleOptions}
        disabled={vehiclesLoading}
      />

      <EditableFormField
        type="select"
        label="Maintenance Category"
        name="category"
        value={form.category}
        onChange={handleChange}
        isEditing={isEditing}
        options={maintenanceCategories}
        required
      />

      <EditableFormField
        type="textarea"
        label="Description"
        name="description"
        value={form.description}
        onChange={handleChange}
        isEditing={isEditing}
        required
        rows={3}
      />

      <EditableFormField
        type="number"
        label="Cost"
        name="cost"
        value={form.cost}
        onChange={handleChange}
        isEditing={isEditing}
        required
      />

      <DatePickerAuto
        editTab={isEditing}
        rest={{}}
        value={form.performedDate}
        label="Performed Date"
        sx={{width: '100%', mt: "16px", mb: "8px"}}
        onUpdate={(date) => {
          setForm(prev => ({ ...prev, performedDate: date }));
        }}
      />

      <DatePickerAuto
        editTab={isEditing}
        rest={{}}
        value={form.scheduledDate}
        label="Scheduled Date"
        sx={{width: '100%', mt: "16px", mb: "8px"}}
        onUpdate={(date) => {
          setForm(prev => ({ ...prev, scheduledDate: date }));
        }}
      />

      {/* Action Buttons - Bottom */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button onClick={handleCancel} sx={{ mr: 1 }} disabled={creating || updating}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={creating || updating}>
          {recordId ? 'Update' : 'Create'}
        </Button>
      </Box>
    </Box>
  );
};

export default MaintenanceForm; 